import React, { createContext, useContext, useMemo } from 'react';

import { DEFAULT_STATE_COPY } from '../constants';

/**
 * The copy a widget shows when it has no rows or could not run (CT-5885).
 *
 * The package ships English, frozen into `DEFAULT_STATE_COPY`, and three
 * different places reached for it directly: `WidgetWrapper`, `CatalogWidget`'s
 * empty fallback and `WidgetNothingToDraw`. A host app had no way to translate
 * any of them, so a Spanish session read "No data to display." on every empty
 * card.
 *
 * A context rather than a prop because the three are at different depths and
 * two of them render inside `CatalogWidget`, which is used outside the widget
 * grid — the library's detail preview and the builder's live preview both draw
 * through it with no provider above them. A prop would have to be threaded
 * through every catalogue widget to reach `WidgetNothingToDraw`.
 *
 * Optional by design: with no provider the defaults apply, so every existing
 * consumer renders exactly as before.
 */
const WidgetStateCopyContext = createContext(null);

/**
 * @param {object} props
 * @param {object} [props.value] Partial copy, `{ EMPTY, CONNECTION_ERROR,
 *   SYSTEM_ERROR }`, each with any of `title` / `message` / `buttonText`.
 *   Anything omitted keeps the package's default for that state, so an app can
 *   translate one state without blanking the others.
 */
export const WidgetStateCopyProvider = ({ value, children }) => {
  const merged = useMemo(() => {
    if (!value) return DEFAULT_STATE_COPY;
    return Object.fromEntries(
      Object.entries(DEFAULT_STATE_COPY).map(([state, fallback]) => [
        state,
        { ...fallback, ...(value[state] ?? {}) },
      ]),
    );
  }, [value]);

  return (
    <WidgetStateCopyContext.Provider value={merged}>
      {children}
    </WidgetStateCopyContext.Provider>
  );
};

/** The state copy in force here: the app's, or the package's English. */
export const useWidgetStateCopy = () => useContext(WidgetStateCopyContext) ?? DEFAULT_STATE_COPY;

export default WidgetStateCopyContext;
